import React from "react";
import { FlaskConical, Apple, HeartPulse, Moon, Brain, Circle, Scale, Activity } from "lucide-react";

/**
 * TwinAttributionBadge — рендерира малки чипове, показващи кои Дигитални
 * Близнаци (Lab, Nutrition, Sleep, ...) захранват дадена секция на BHI.
 *
 * Props:
 *  - twins: array of twin ids (e.g. ["lab_twin", "nutrition_twin"])
 *  - registry: TWIN_REGISTRY обект от backend (snapshot.twin_attribution.twin_registry)
 *  - markers: optional array of marker labels (shown as tooltip)
 *  - size: "sm" | "xs"  (default "xs")
 *  - lang: "bg" | "en" | "es"
 */

const ICONS = { FlaskConical, Apple, HeartPulse, Moon, Brain, Circle, Scale, Activity };

const COLOR_MAP = {
  indigo:  { bg: "bg-indigo-100 dark:bg-indigo-500/20",   text: "text-indigo-700 dark:text-indigo-300" },
  emerald: { bg: "bg-emerald-100 dark:bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-300" },
  rose:    { bg: "bg-rose-100 dark:bg-rose-500/20",       text: "text-rose-700 dark:text-rose-300" },
  violet:  { bg: "bg-violet-100 dark:bg-violet-500/20",   text: "text-violet-700 dark:text-violet-300" },
  amber:   { bg: "bg-amber-100 dark:bg-amber-500/20",     text: "text-amber-700 dark:text-amber-300" },
  pink:    { bg: "bg-pink-100 dark:bg-pink-500/20",       text: "text-pink-700 dark:text-pink-300" },
  sky:     { bg: "bg-sky-100 dark:bg-sky-500/20",         text: "text-sky-700 dark:text-sky-300" },
  teal:    { bg: "bg-teal-100 dark:bg-teal-500/20",       text: "text-teal-700 dark:text-teal-300" },
};

export default function TwinAttributionBadge({
  twins = [],
  registry = {},
  markers = [],
  size = "xs",
  lang = "bg",
}) {
  if (!twins || twins.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="twin-attribution">
      <span className="text-[10px] opacity-60 mr-1 uppercase tracking-wider font-semibold">
        {lang === "bg" ? "Източници:" : lang === "es" ? "Fuentes:" : "Sources:"}
      </span>
      {twins.map((twinId) => {
        const meta = registry[twinId] || {};
        const Icon = ICONS[meta.icon] || FlaskConical;
        const c = COLOR_MAP[meta.color] || COLOR_MAP.indigo;
        const label = meta.label_bg || twinId;
        const tooltipText = markers.length
          ? `${label} — ${markers.join(", ")}`
          : label;
        return (
          <span
            key={twinId}
            title={tooltipText}
            data-testid={`twin-chip-${twinId}`}
            className={`inline-flex items-center gap-1 rounded-full ${c.bg} ${c.text} font-medium ${
              size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-1.5 py-0.5 text-[10px]"
            }`}
          >
            <Icon className={size === "sm" ? "w-3 h-3" : "w-2.5 h-2.5"} />
            {label}
          </span>
        );
      })}
    </div>
  );
}
